import type { HTMLAttributes, ReactNode, TableHTMLAttributes } from 'react';
import classNames from 'classnames';
import styles from './table.module.scss';

interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode;
}

function Table({ children, className, ...props }: TableProps) {
  return (
    <table className={classNames(styles.table, className)} {...props}>
      {children}
    </table>
  );
}

interface HeaderProps extends HTMLAttributes<HTMLTableSectionElement> {}

function Header({ children, className, ...props }: HeaderProps) {
  return (
    <thead className={classNames(styles.tableHeader, className)} {...props}>
      {children}
    </thead>
  );
}

interface HeaderCellProps extends HTMLAttributes<HTMLTableCellElement> {}

function HeaderCell({ children, className, ...props }: HeaderCellProps) {
  return (
    <th className={classNames(styles.tableHeaderCell, className)} {...props}>
      {children}
    </th>
  );
}

interface BodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

function Body({ children, className, ...props }: BodyProps) {
  return (
    <tbody className={classNames(styles.tableBody, className)} {...props}>
      {children}
    </tbody>
  );
}

interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  gridTemplateColumns?: string;
}

function Row({ children, gridTemplateColumns, className, ...props }: RowProps) {
  return (
    <tr
      className={classNames(styles.tableRow, className)}
      style={{
        ...(gridTemplateColumns && { gridTemplateColumns }),
      }}
      {...props}
    >
      {children}
    </tr>
  );
}

interface CellProps extends HTMLAttributes<HTMLTableCellElement> {}

function Cell({ children, className, ...props }: CellProps) {
  return (
    <td className={classNames(styles.tableCell, className)} {...props}>
      {children}
    </td>
  );
}

interface MobileListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function MobileList({ children, className, ...props }: MobileListProps) {
  return (
    <div className={classNames(styles.mobileList, className)} {...props}>
      {children}
    </div>
  );
}

interface MobileCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function MobileCard({ children, className, ...props }: MobileCardProps) {
  return (
    <div
      className={classNames(styles.mobileCardContainer, className)}
      {...props}
    >
      <div className={styles.mobileCardInnerContainer}>{children}</div>
    </div>
  );
}

interface MobileItemProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  children: ReactNode;
}

function MobileItem({ label, children, className, ...props }: MobileItemProps) {
  return (
    <div className={classNames(styles.mobileCardItem, className)} {...props}>
      <strong>{label}</strong>
      <div className={styles.mobileCardItemValue}>{children}</div>
    </div>
  );
}

Table.Header = Header;
Table.HeaderCell = HeaderCell;
Table.Body = Body;
Table.Row = Row;
Table.Cell = Cell;
Table.MobileList = MobileList;
Table.MobileCard = MobileCard;
Table.MobileItem = MobileItem;

export { Table };
